import React, { useEffect, useState, useContext } from "react";
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Avatar,
  makeStyles
} from "@material-ui/core";
import {
  PlayArrow,
  Pause,
  Error,
  Search,
  Refresh
} from "@material-ui/icons";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import api from "../../services/api";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: 24,
    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
    minHeight: "100vh",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  filterPaper: {
    padding: 16,
    marginBottom: 24,
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    background: "white",
  },
  stepColumn: {
    minWidth: 340,
    maxWidth: 340,
    marginRight: 16,
    backgroundColor: "white",
    borderRadius: 12,
    padding: 16,
    height: "calc(100vh - 280px)",
    overflowY: "auto",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
    },
  },
  stepHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: 16,
    padding: 12,
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 14,
  },
  contactCard: {
    marginBottom: 12,
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: "1px solid #e8ecf1",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      transform: "translateY(-2px)",
    },
  },
  contactCardError: {
    borderLeft: "4px solid #f44336",
  },
  contactCardSuccess: {
    borderLeft: "4px solid #4caf50",
  },
  kanbanContainer: {
    display: "flex",
    overflowX: "auto",
    paddingBottom: 16,
    gap: 16,
  },
  emptyState: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 400,
    color: "#999",
  },
}));

const FloupDashboard = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [floups, setFloups] = useState([]);
  const [selectedFloupId, setSelectedFloupId] = useState(null);
  const [floupData, setFloupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingFloups, setLoadingFloups] = useState(true);
  const [filters, setFilters] = useState({
    channel: "",
    search: ""
  });

  // Canais disponíveis
  const channels = ["whatsapp", "whatsapp_oficial", "facebook", "instagram"];

  const loadFloups = async () => {
    try {
      setLoadingFloups(true);
      const { data } = await api.get("/plugins/floup");
      setFloups(data || []);
      // Se não há Floup selecionado e há Floups disponíveis, selecionar o primeiro
      if (data && data.length > 0) {
        setSelectedFloupId((prev) => prev || data[0].id);
      }
    } catch (error) {
      console.error("Erro ao carregar Floups:", error);
    } finally {
      setLoadingFloups(false);
    }
  };

  const loadFloupDashboard = async () => {
    if (!selectedFloupId) {
      setFloupData(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.channel) params.append("channel", filters.channel);
      if (filters.search) params.append("search", filters.search);

      const { data } = await api.get(`/plugins/floup/dashboard/${selectedFloupId}?${params.toString()}`);
      setFloupData(data);
    } catch (error) {
      console.error("Erro ao carregar dashboard do Floup:", error);
      setFloupData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFloups();
  }, []);

  useEffect(() => {
    loadFloupDashboard();
    
    // Atualizar a cada 30 segundos
    const interval = setInterval(() => {
      loadFloupDashboard();
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFloupId, filters]);

  // Listener de socket para atualizar quando um Floup é cancelado
  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const companyId = user.companyId;
    const eventName = `company-${companyId}-floup-stopped`;

    const onFloupStopped = (data) => {
      console.log('[FLOUP DASHBOARD] Socket event floup-stopped recebido', data);
      // Se o evento é para o Floup selecionado (ou não há Floup selecionado), recarregar imediatamente
      if (!selectedFloupId || data.floupId === selectedFloupId) {
        console.log('[FLOUP DASHBOARD] Floup cancelado, recarregando dashboard...');
        // Aguardar um pouco para garantir que o backend processou
        setTimeout(() => {
          loadFloupDashboard();
        }, 500);
      }
    };

    socket.on(eventName, onFloupStopped);

    return () => {
      socket.off(eventName, onFloupStopped);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user?.companyId, selectedFloupId, loadFloupDashboard]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleFloupChange = (floupId) => {
    setSelectedFloupId(floupId);
    setFloupData(null);
  };

  const renderContactCard = (contact) => {
    const handleContactClick = () => {
      console.log('[FLOUP DASHBOARD] Clicou no contato:', {
        contactId: contact.contactId,
        ticketUuid: contact.ticketUuid,
        ticketId: contact.ticketId,
        ticketStatus: contact.ticketStatus
      });
      
      if (contact.ticketUuid) {
        // Mapear status do ticket para a aba correta
        // pending = "Aguardando", open = "Atendendo", etc.
        let tabParam = '';
        if (contact.ticketStatus) {
          // Mapear status do backend para as abas do frontend
          const statusMap = {
            'pending': 'pending',
            'open': 'open',
            'closed': 'closed',
            'group': 'group'
          };
          const mappedStatus = statusMap[contact.ticketStatus] || contact.ticketStatus;
          tabParam = `?tab=${mappedStatus}`;
          console.log('[FLOUP DASHBOARD] Redirecionando com tab:', mappedStatus, 'URL:', `/tickets/${contact.ticketUuid}${tabParam}`);
        } else {
          console.warn('[FLOUP DASHBOARD] ticketStatus não encontrado para o contato:', contact.contactId);
        }
        // Redirecionar diretamente para o ticket usando UUID com parâmetro de aba
        history.push(`/tickets/${contact.ticketUuid}${tabParam}`);
      } else if (contact.ticketId) {
        // Se não tiver UUID mas tiver ticketId, buscar o UUID primeiro
        // Por enquanto, redirecionar para lista de tickets com filtro
        let tabParam = '';
        if (contact.ticketStatus) {
          tabParam = `&tab=${contact.ticketStatus}`;
        }
        history.push(`/tickets?contactId=${contact.contactId}${tabParam}`);
      } else {
        // Se não tiver ticketId, tentar buscar pelo contato
        history.push(`/tickets?contactId=${contact.contactId}`);
      }
    };

    return (
      <Card
        key={contact.contactId}
        className={`${classes.contactCard} ${contact.hasRecentError ? classes.contactCardError : classes.contactCardSuccess}`}
        onClick={handleContactClick}
      >
        <CardContent style={{ padding: 12 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Avatar
              src={contact.contactProfilePicUrl}
              style={{
                width: 40,
                height: 40
              }}
            >
              {contact.contactName ? contact.contactName.charAt(0).toUpperCase() : "?"}
            </Avatar>
            <Box flex={1}>
              <Typography variant="h6" style={{ fontSize: 14, fontWeight: 700 }}>
                {contact.contactName}
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ fontSize: 11 }}>
                {contact.contactNumber}
              </Typography>
            </Box>
            {contact.hasRecentError && (
              <Chip
                size="small"
                label="Erro"
                style={{
                  backgroundColor: "#f44336",
                  color: "#fff",
                  fontWeight: 600,
                  height: 20,
                }}
              />
            )}
          </Box>

          <Divider style={{ margin: "8px 0" }} />

          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Chip
              size="small"
              label={contact.channel}
              style={{ height: 18, fontSize: 9, fontWeight: 600 }}
            />
          </Box>

          {contact.nextRunAt && (
            <Box mt={1}>
              <Typography variant="caption" color="textSecondary" style={{ fontSize: 10 }}>
                Próxima: {format(new Date(contact.nextRunAt), "dd/MM 'às' HH:mm", { locale: ptBR })}
              </Typography>
            </Box>
          )}

          {contact.hasRecentError && (
            <Box mt={1} p={1} style={{ backgroundColor: "#ffebee", borderRadius: 4 }}>
              <Typography variant="caption" style={{ color: "#f44336", fontWeight: 600, fontSize: 10 }}>
                ⚠️ Erro recente
              </Typography>
              {contact.lastError && (
                <Typography variant="caption" style={{ color: "#d32f2f", fontSize: 9, display: "block", marginTop: 4 }}>
                  {contact.lastError}
                </Typography>
              )}
              {contact.retryCount && (
                <Typography variant="caption" style={{ color: "#f57c00", fontSize: 9, display: "block", marginTop: 2 }}>
                  Tentativas: {contact.retryCount}/3
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderStepColumn = (step) => {
    const contacts = step.contacts || [];
    const stepColor = step.contactsCount > 0 ? "#4caf50" : "#9e9e9e";

    return (
      <Box
        key={step.stepIndex}
        className={classes.stepColumn}
      >
        <Box
          className={classes.stepHeader}
          style={{
            backgroundColor: `${stepColor}15`,
            borderLeft: `4px solid ${stepColor}`
          }}
        >
          <Typography variant="h6" style={{ fontWeight: 700, fontSize: 13 }}>
            Etapa {step.stepOrder}
          </Typography>
          <Chip
            size="small"
            label={contacts.length}
            style={{
              marginLeft: "auto",
              backgroundColor: stepColor,
              color: "#fff",
              fontWeight: 600,
              height: 22,
            }}
          />
        </Box>
        
        <Box mb={2} p={1} style={{ backgroundColor: "#f9fafb", borderRadius: 6, marginBottom: 12, border: "1px solid #e8ecf1" }}>
          <Typography variant="caption" color="textSecondary" style={{ fontSize: 11, fontWeight: 500 }}>
            {step.stepMessage ? step.stepMessage.substring(0, 100) + (step.stepMessage.length > 100 ? '...' : '') : 'Sem mensagem'}
          </Typography>
        </Box>

        {contacts.length === 0 ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ height: 100, color: "#bbb", textAlign: "center" }}
          >
            <Typography variant="body2">Nenhum contato nesta etapa</Typography>
          </Box>
        ) : (
          contacts.map(renderContactCard)
        )}
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      <div className={classes.header}>
        <h1 className={classes.title}>🔄 Dashboard de Follow UP</h1>
        <Tooltip title="Atualizar">
          <IconButton onClick={loadFloupDashboard} disabled={loading} style={{ color: "#3b82f6" }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </div>

      {/* Seletor de Floup e Filtros */}
      <Paper className={classes.filterPaper}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel style={{ fontSize: 12 }}>Follow UP</InputLabel>
              <Select
                value={selectedFloupId || ""}
                onChange={(e) => handleFloupChange(e.target.value)}
                label="Follow UP"
                disabled={loadingFloups}
                style={{ fontSize: 13 }}
              >
                {loadingFloups ? (
                  <MenuItem value="">
                    <CircularProgress size={16} />
                  </MenuItem>
                ) : (
                  floups.map((floup) => (
                    <MenuItem key={floup.id} value={floup.id} style={{ fontSize: 13 }}>
                      {floup.name} {floup.isActive ? "" : "(Inativo)"}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          {floupData && (
            <>
              <Grid item xs={6} sm={2} md={1.5}>
                <Box>
                  <Typography variant="caption" color="textSecondary" style={{ fontSize: 10, fontWeight: 600 }}>
                    Etapas
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 700, fontSize: 16, color: "#3b82f6" }}>
                    {floupData.totalSteps}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={2} md={1.5}>
                <Box>
                  <Typography variant="caption" color="textSecondary" style={{ fontSize: 10, fontWeight: 600 }}>
                    Contatos
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 700, fontSize: 16, color: "#10b981" }}>
                    {floupData.totalContacts}
                  </Typography>
                </Box>
              </Grid>
            </>
          )}
          {floupData && (
            <>
              <Grid item xs={12} sm={3} md={2.5}>
                <TextField
                  fullWidth
                  label="Buscar"
                  variant="outlined"
                  size="small"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  InputProps={{
                    startAdornment: <Search style={{ marginRight: 8, color: "#999", fontSize: 18 }} />,
                    style: { fontSize: 13 }
                  }}
                  InputLabelProps={{ style: { fontSize: 12 } }}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={2.5}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel style={{ fontSize: 12 }}>Canal</InputLabel>
                  <Select
                    value={filters.channel}
                    onChange={(e) => handleFilterChange("channel", e.target.value)}
                    label="Canal"
                    style={{ fontSize: 13 }}
                  >
                    <MenuItem value="" style={{ fontSize: 13 }}>Todos</MenuItem>
                    {channels.map((channel) => (
                      <MenuItem key={channel} value={channel} style={{ fontSize: 13 }}>
                        {channel.charAt(0).toUpperCase() + channel.slice(1).replace("_", " ")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Kanban Board - Etapas */}
      {loading ? (
        <Box className={classes.emptyState}>
          <CircularProgress />
        </Box>
      ) : !selectedFloupId ? (
        <Box className={classes.emptyState}>
          <Typography variant="h6" color="textSecondary">
            Selecione um Floup para visualizar as etapas
          </Typography>
        </Box>
      ) : !floupData ? (
        <Box className={classes.emptyState}>
          <Typography variant="h6" color="textSecondary">
            Nenhum dado encontrado para este Floup
          </Typography>
        </Box>
      ) : (
        <Box>
          <Box mb={3}>
            <Typography variant="h5" style={{ marginBottom: 8, fontWeight: 700, color: "#1a1a1a" }}>
              {floupData.floupName}
            </Typography>
            {floupData.floupDescription && (
              <Typography variant="body2" color="textSecondary" style={{ fontSize: 13 }}>
                {floupData.floupDescription}
              </Typography>
            )}
          </Box>
          <Box className={classes.kanbanContainer}>
            {floupData.steps && floupData.steps.length > 0 ? (
              floupData.steps.map(renderStepColumn)
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{ width: "100%", height: 200, color: "#999" }}
              >
                <Typography variant="body2">Nenhuma etapa encontrada</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FloupDashboard;

